import React, { useEffect, useRef } from "react";

const AboutSection = () => {
  const sectionRef = useRef(null);
  const badgeRef = useRef(null);
  const headlineRef = useRef(null);
  const storyRef = useRef(null);
  const statsRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-reveal");
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = [
      badgeRef.current,
      headlineRef.current,
      storyRef.current,
      statsRef.current,
      ctaRef.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-white overflow-hidden min-h-screen flex items-center">
      
      {/* ===== LIGHT, AIRY BACKGROUND - MATCHES HERO ===== */}
      <div className="absolute inset-0">
        {/* Soft teal gradient - from hero section */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-teal-50/40 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        
        {/* Warm amber glow - from campaigns section */}
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-gradient-to-tr from-amber-50/40 via-transparent to-transparent rounded-full blur-3xl animate-float-delay"></div>
        
        {/* Soft emerald center - cohesive palette */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-r from-emerald-50/30 via-teal-50/30 to-emerald-50/30 rounded-full blur-3xl animate-pulse-slow"></div>
        
        {/* Delicate dot pattern - from features section */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #14b8a6 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 w-full">
        
        {/* ===== DECORATIVE TOP WAVE - ADDS VISUAL INTEREST ===== */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-24">
          <div className="w-full h-full bg-gradient-to-b from-teal-100/50 to-transparent rounded-full blur-2xl"></div>
        </div>

        {/* ===== HEADLINE - GRADIENT TEXT WITH DECORATION ===== */}
        <div className="relative mb-6 md:mb-8">
          <h2 
            ref={headlineRef}
            className="opacity-0 translate-y-8 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight"
          >
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                A vision
              </span>
              <span className="absolute -bottom-2 left-0 w-full h-3 bg-gradient-to-r from-teal-200/50 to-emerald-200/50 blur-sm"></span>
            </span>
            <br />
            <span className="relative inline-block mt-2">
              <span className="relative z-10 text-gray-800">
                to reimagine giving.
              </span>
              <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-teal-400 to-emerald-400"></span>
            </span>
          </h2>
          
          {/* Decorative floating elements - fills empty space */}
          <div className="absolute -right-12 top-0 hidden lg:block">
            <div className="relative">
              <div className="w-20 h-20 bg-teal-100/30 rounded-full blur-xl animate-float"></div>
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-amber-100/30 rounded-full blur-lg animate-float-delay"></div>
            </div>
          </div>
        </div>

        {/* ===== STORY CARD - LIGHT, AIRY, FULL OF DETAIL ===== */}
        <div 
          ref={storyRef}
          className="opacity-0 translate-y-8 mb-8 md:mb-10"
        >
          <div className="relative group">
            {/* Soft glow on hover */}
            <div className="absolute -inset-2 bg-gradient-to-r from-teal-100/50 to-emerald-100/50 rounded-3xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
            
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-white/60 shadow-xl shadow-teal-100/30">
              
              {/* Decorative corner accents - soft */}
              <div className="absolute top-4 left-4 w-12 h-12 border-l-2 border-t-2 border-teal-200/70 rounded-tl-3xl"></div>
              <div className="absolute bottom-4 right-4 w-12 h-12 border-r-2 border-b-2 border-emerald-200/70 rounded-br-3xl"></div>
              
              {/* Floating paper planes - adds movement, fills space */}
              <div className="absolute -top-8 -right-8 text-4xl opacity-20 group-hover:opacity-40 transition-opacity duration-700 animate-float-slow">
                ✈️
              </div>
              <div className="absolute -bottom-8 -left-8 text-4xl opacity-20 group-hover:opacity-40 transition-opacity duration-700 animate-float-delay">
                ✨
              </div>
              
              {/* Opening quote - soft teal */}
              <span className="text-7xl font-serif text-teal-200/70 leading-none absolute -top-3 left-4">"</span>
              
              {/* Story content - with rich formatting */}
              <div className="space-y-3 text-gray-700 text-base md:text-xl leading-relaxed relative z-10 px-2 md:px-6">
                <p className="font-medium text-teal-700 text-lg md:text-2xl">
                  DonatePe was born from a vision to reimagine charitable giving.
                </p>
                
                <div className="flex items-start gap-3">
                  <div className="w-1 h-12 bg-gradient-to-b from-teal-400 to-emerald-400 rounded-full mt-1"></div>
                  <p className="text-gray-600 text-sm md:text-base">
                    As crowdfunding began revolutionizing how people supported material needs,{' '}
                    <span className="font-semibold text-teal-600">we saw an opportunity to create something more</span>—
                    a platform where hope and spiritual renewal could flourish alongside tangible aid.
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-teal-50/50 to-emerald-50/50 p-4 rounded-2xl border border-teal-100/50">
                  <p className="italic text-gray-700 text-sm md:text-base">
                    "After navigating challenges and refining our approach, we launched our platform
                    ready to connect compassionate donors with causes that matter."
                  </p>
                </div>
              </div>
              
              {/* Closing quote - soft emerald */}
              <span className="absolute bottom-2 right-6 text-7xl font-serif text-emerald-200/70 leading-none rotate-180">"</span>
              
              {/* Signature line */}
              <div className="absolute bottom-4 left-6 flex items-center gap-2">
                <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white text-[10px] md:text-xs font-bold shadow-md">
                  DP
                </div>
                <span className="text-[10px] md:text-xs text-gray-400">— The DonatePe team</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== STATS - LIGHT, MODERN, FULL OF DETAIL ===== */}
        

        {/* ===== CTA - KNOW MORE BUTTON ===== */}
        <div 
          ref={ctaRef}
          className="opacity-0 translate-y-8 flex flex-col items-center w-full px-0 pb-4 md:pb-6"
        >
          {/* Decorative rings */}
          <div className="relative w-full flex justify-center">
            <div className="absolute -inset-4 sm:-inset-5 bg-gradient-to-r from-teal-100/80 to-emerald-100/80 rounded-full blur-xl sm:blur-2xl"></div>
            <div className="absolute -inset-2 sm:-inset-3 bg-white/95 rounded-full blur-md"></div>
            
            <a 
              href="/about" 
              className="relative flex items-center justify-center gap-2 sm:gap-3 px-10 sm:px-12 py-5 sm:py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full font-semibold text-lg sm:text-xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl hover:shadow-teal-200/50 transition-all duration-500 hover:scale-105 group min-w-[200px] sm:min-w-[220px]"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap">
                <span>Know More</span>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-2 transition-transform duration-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              
              {/* Shine effect */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-reveal {
          animation: reveal 0.9s cubic-bezier(0.23, 1, 0.32, 1) forwards;
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.4;
          }
          33% { 
            transform: translate(20px, -15px) scale(1.05); 
            opacity: 0.5;
          }
          66% { 
            transform: translate(-15px, 10px) scale(0.95); 
            opacity: 0.45;
          }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        .animate-float-delay {
          animation: float 25s ease-in-out infinite reverse;
        }
        
        .animate-float-slow {
          animation: float 30s ease-in-out infinite;
        }
        
        @keyframes pulse-slow {
          0%, 100% { 
            opacity: 0.2; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.3; 
            transform: scale(1.05); 
          }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default AboutSection;